import { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { navigate, useNavigationPrompt, useQueryParams } from 'raviger';
import { Field, Form, Formik } from "formik";
import Select from 'react-select';
import { ButtonGroup, Card, Col, Image, Form as BootstrapForm, Row } from "react-bootstrap";
import * as Yup from 'yup';
import { Switch } from 'formik-mui';
import { AddressSearch, Checkbox, DateTimePicker, DropDown, FileUploader, ImageUploader, TextInput } from '../components/Form.js';
import { catAgeChoices, dogAgeChoices, horseAgeChoices, otherAgeChoices, catColorChoices, dogColorChoices, horseColorChoices, otherColorChoices, sexChoices, dogSizeChoices, catSizeChoices, horseSizeChoices, otherSizeChoices, reportedStatusChoices, statusChoicesNFA, unknownChoices, otherAgeChoice } from './constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft, faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import ButtonSpinner from "../components/ButtonSpinner";
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import CustomSelect from "../components/CustomSelect.js";

const AnimalForm = (props) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const id = props.id;

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    owner_id = null,
  } = queryParams;

  // Track species selected and update choice lists accordingly.
  const speciesRef = useRef(null);
  const sexRef = useRef(null);
  const sizeRef = useRef(null);
  const ageTextRef = useRef();
  const pcolorRef = useRef(null);
  const scolorRef = useRef(null);
  const roomRef = useRef(null);
  const shelterRef = useRef(null);
  const ageChoices = {'':[], 'dog':dogAgeChoices, 'cat':catAgeChoices, 'horse':horseAgeChoices, 'other':otherAgeChoices}
  const colorChoices = {'':[], 'dog':dogColorChoices, 'cat':catColorChoices, 'horse':horseColorChoices, 'other':otherColorChoices}
  const sizeChoices = {'':[], 'dog':dogSizeChoices, 'cat':catSizeChoices, 'horse':horseSizeChoices, 'other':otherSizeChoices}

  // Dynamic placeholder value for options.
  const [placeholder, setPlaceholder] = useState("Select a species...");

  const initialData = {
    new_owner: owner_id,
    name: '',
    species: '',
    sex: '',
    size: '',
    age: '',
    pcolor: '',
    scolor: '',
    color_notes: '',
    fixed: 'unknown',
    aco_required: 'unknown',
    aggressive: 'unknown',
    confined: 'unknown',
    injured: 'unknown',
    behavior_notes: '',
    medical_notes: '',
    microchip: '',
    animal_count: 0,
    external_id: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: null,
    longitude: null,
    organization_slug: props.organization,
  }

  // Initial Animal data.
  const [data, setData] = useState(initialData);
  const [species, setSpecies] = useState({options: []});

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchSpecies = () => {
      setSpecies({options: []});
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          response.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.id, label: result.name});
          });
          setSpecies({options: species_options});
        }
      })
      .catch(error => {
        if (!unmounted) {
          setSpecies({options: []});
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    if (id) {
      const fetchAnimalData = async () => {
        // Fetch Animal data.
        await axios.get('/animals/api/prereg/' + id + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setData(response.data);
            setPlaceholder("Select...");
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchAnimalData();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id]);
  
  return (
    <>
      <Formik
        initialValues={data}
        enableReinitialize={true}
        validationSchema={Yup.object({
          status: Yup.string(),
          name: Yup.string()
            .max(30, 'Must be 30 characters or less.'),
          species: Yup.number()
            .required('Required'),
          species_string: Yup.string(),
          size: Yup.string(),
          age: Yup
            .string()
            .max(10, 'Must be 10 characters or less'),
          ageText: Yup
            .string()
            .max(10, 'Must be 10 characters or less'),
          sex: Yup.string()
            .oneOf(['M', 'F']),
          animal_count: Yup.number('Value must be a number').required('Required').positive('Value must be positive').integer('Value must be a whole number'),
          external_id: Yup.string(),
          pcolor: Yup.string(),
          scolor: Yup.string(),
          color_notes: Yup.string()
            .max(400, 'Must be 400 characters or less'),
          fixed: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          aco_required: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          aggressive: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          confined: Yup.string()
           .max(10, 'Must be 10 characters or less'),
          injured: Yup.string()
           .max(10, 'Must be 10 characters or less'),
          behavior_notes: Yup.string()
            .max(400, 'Must be 400 characters or less'),
          microchip: Yup.string().max(50, 'Must be 50 characters or less.'),
          front_image: Yup.mixed(),
          side_image: Yup.mixed(),
          extra_images: Yup.array(),
          address: Yup.string(),
          city: Yup.string().nullable(),
          state: Yup.string(),
          zip_code: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          priority: Yup.string(),
          presenting_complaints: Yup.array().when('priority', {
            is: value => value && value !== 'green',
            then: Yup.array().min(1, 'Required')
          }),
          latitude: Yup.number()
            .nullable(),
          longitude: Yup.number()
            .nullable()
        })}
        validate={(values) => {
          const errors = {};
          if (
            values.age === otherAgeChoice.value
            && !values.ageText?.trim()
          ) {
            errors.ageText = 'Input required'
          }
          return errors;
        }}
        onSubmit={ async (values, { resetForm }) => {
          // Remove owners from form data.
          if (values["owners"]) {
            delete values["owners"];
          }
          // handle age or ageText
          if (values.age === otherAgeChoice.value) {
            values.age = values.ageText;
          }

          if (id) {
            axios.put('/animals/api/prereg/' + id + '/', values)
            .then(function() {
              navigate('/' + props.organization + '/manage/animals/' + id);
            })
            .catch(error => {
              setShowSystemError(true);
            });
          }
          else {
            axios.post('/animals/api/prereg/', values)
            .then(response => {
              if (owner_id) {
                navigate('/' + props.organization + '/manage/owners/' + owner_id);
              }
              else {
                navigate('/' + props.organization + '/manage/animals/' + response.data.id);
              }
            })
            .catch(error => {
              setShowSystemError(true);
            });
          }
        }}
      >
        {formikProps => (
          <Card border="secondary" style={{marginTop:"35px"}}>
            <Card.Header as="h5" className="ps-3">
              <span id="back" style={{cursor:'pointer'}} onClick={() => owner_id ? navigate('/' + props.organization + '/manage/owners/' + owner_id) : navigate('/' + props.organization + '/manage/animals/' + id)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>Preregistered Animal Form
            </Card.Header>
            <Card.Body>
            <BootstrapForm as={Form}>
                <Row className="gx-2">
                  <TextInput
                    id="name"
                    name="name"
                    type="text"
                    label="Animal Name"
                    xs="4"
                  />
                  <Col xs="3" style={{textTransform:'capitalize'}}>
                    <DropDown
                      label="Species"
                      id="speciesDropdown"
                      name="species"
                      type="text"
                      key={`my_unique_species_select_key__${formikProps.values.species}`}
                      ref={speciesRef}
                      options={species.options}
                      value={formikProps.values.species||''}
                      isClearable={(formikProps.values.id) ? false : true}
                      onChange={(instance) => {
                        setPlaceholder("Select...");
                        sizeRef.current.clearValue();
                        // ageRef.current.clearValue();
                        pcolorRef.current.clearValue();
                        scolorRef.current.clearValue();
                        formikProps.setFieldValue("species", instance ? instance.value : '');
                        formikProps.setFieldValue("species_string", instance ? instance.label : '');
                        formikProps.setFieldValue("animal_count", instance ? formikProps.values.animal_count ? formikProps.values.animal_count : 1 : 0);
                      }}
                    />
                  </Col>
                  <Col xs="3">
                    <DropDown
                      label="Size"
                      id="sizeDropdown"
                      name="size"
                      type="text"
                      isClearable={true}
                      key={`my_unique_size_select_key__${formikProps.values.size}`}
                      ref={sizeRef}
                      options={Object.keys(sizeChoices).includes(formikProps.values.species_string) ? sizeChoices[formikProps.values.species_string] : sizeChoices['other']}
                      value={formikProps.values.size||''}
                      placeholder={placeholder}
                    />
                  </Col>
                  <TextInput
                    id="animal_count"
                    name="animal_count"
                    type="text"
                    xs="2"
                    label="No. of Animals"
                    onChange={(instance) => {
                      formikProps.setFieldValue("animal_count", instance === null && typeof Number(instance.target.value) === 'number' ? '' : Number(instance.target.value));
                      if (instance.target.value && typeof Number(instance.target.value) === 'number' && Number(instance.target.value) > 1) {
                        formikProps.setFieldValue("priority", 'green');
                      }
                    }}
                  />
                </Row>
                <Row className="mt-3 gx-2">
                  <Col xs="4">
                    <DropDown
                      label="Primary Color"
                      id="pcolor"
                      name="pcolor"
                      type="text"
                      key={`my_unique_pcolor_select_key__${formikProps.values.pcolor}`}
                      ref={pcolorRef}
                      style={{marginTop:"2px"}}
                      options={Object.keys(colorChoices).includes(formikProps.values.species_string) ? colorChoices[formikProps.values.species_string] : colorChoices['other']}
                      value={formikProps.values.pcolor||''}
                      placeholder={placeholder}
                    />
                    <DropDown
                      label="Secondary Color"
                      id="scolor"
                      name="scolor"
                      type="text"
                      key={`my_unique_scolor_select_key__${formikProps.values.scolor}`}
                      ref={scolorRef}
                      style={{marginTop:"29px"}}
                      options={Object.keys(colorChoices).includes(formikProps.values.species_string) ? colorChoices[formikProps.values.species_string] : colorChoices['other']}
                      value={formikProps.values.scolor||''}
                      placeholder={placeholder}
                    />
                  </Col>
                  <TextInput
                    id="color_notes"
                    name="color_notes"
                    as="textarea"
                    rows={5}
                    label="Breed / Description"
                    xs="8"
                  />
                </Row>
                <Row className="mt-3 mb-3 gx-2">
                  <Col xs="3">
                    <DropDown
                      label="Sex"
                      id="sexDropDown"
                      name="sex"
                      type="text"
                      key={`my_unique_sex_select_key__${formikProps.values.sex}`}
                      ref={sexRef}
                      options={sexChoices}
                      value={formikProps.values.sex||''}
                    />
                  </Col>
                  <Col xs="3">
                    <CustomSelect
                      label="Age"
                      options={Object.keys(ageChoices).includes(formikProps.values.species_string)
                        ? ageChoices[formikProps.values.species_string]
                        : ageChoices['other']}
                      value={formikProps.values.age || ''}
                      handleValueChange={(value) => formikProps.setFieldValue('age', value)}
                      optionsKey={formikProps.values.species_string || ''}
                      formValidationName="age"
                    />
                  </Col>
                  <Col xs="3">
                    <DropDown
                      label="Fixed"
                      id="fixed"
                      name="fixed"
                      type="text"
                      options={unknownChoices}
                      value={formikProps.values.fixed||'unknown'}
                      isClearable={false}
                    />
                  </Col>
                </Row>
                <Row className="gx-2">
                  <Col xs="3">
                    <DropDown
                      label="Aggressive"
                      id="aggressive"
                      name="aggressive"
                      type="text"
                      options={unknownChoices}
                      value={formikProps.values.aggressive||'unknown'}
                      isClearable={false}
                      onChange={(instance) => {
                        formikProps.setFieldValue("aggressive", instance === null ? '' : instance.value);
                        formikProps.setFieldValue("aco_required", instance && instance.value === 'yes' ? 'yes' : formikProps.values.aco_required);
                      }}
                    />
                  </Col>
                  <Col xs="3">
                    <DropDown
                      label="Special Handling Required"
                      id="aco_required"
                      name="aco_required"
                      type="text"
                      options={unknownChoices}
                      value={formikProps.values.aco_required||'unknown'}
                      isClearable={false}
                    />
                  </Col>
                  <Col xs="3">
                    <DropDown
                      label="Confined"
                      id="confined"
                      name="confined"
                      type="text"
                      options={unknownChoices}
                      value={formikProps.values.confined||'unknown'}
                      isClearable={false}
                    />
                  </Col>
                  <Col xs="3">
                    <DropDown
                      label="Injured"
                      id="injured"
                      name="injured"
                      type="text"
                      options={unknownChoices}
                      value={formikProps.values.injured||'unknown'}
                      isClearable={false}
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <TextInput
                    label="Animal Notes"
                    id="behavior_notes"
                    name="behavior_notes"
                    as="textarea"
                    rows={5}
                    xs="12"
                  />
                </Row>
                <Row className="mt-3">
                  <TextInput
                    label="Medical Notes"
                    id="medical_notes"
                    name="medical_notes"
                    as="textarea"
                    rows={5}
                    xs="12"
                  />
                </Row>
                <Row className={"mt-3"}>
                  <TextInput
                    id="microchip"
                    name="microchip"
                    type="text"
                    label="Microchip Number"
                    xs="6"
                  />
                </Row>
                <Row className="mt-3">
                  <TextInput
                    id="external_id"
                    name="external_id"
                    type="text"
                    xs="6"
                    label="External ID"
                  />
                </Row>
            </BootstrapForm>
          </Card.Body>
          <ButtonGroup size="lg">
            <ButtonSpinner isSubmitting={formikProps.isSubmitting} isSubmittingText="Saving..." type="button" onClick={() => {
              formikProps.submitForm();
            }}>
              Save
            </ButtonSpinner>
          </ButtonGroup>
          </Card>
        )}
      </Formik>
    </>
  );
};

export default AnimalForm
